import type { World } from '../sim/types';
import { DEPTH_BINS, ISLAND_NAMES, REEF_ISLANDS, stratumFloorKm2, stratumIndex, type Reef, type ReefIsland } from '../sim/reef';
import { reefOf } from '../sim/data/sites';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import { knownAt, type AreaCount } from '../sim/areas';
import { REEF_BOTTOM } from '../sim/data/reef/bottom';
import { heatStressToday } from '../sim/systems/heatStress';
import { HEAT_ALERT_DHW, isSurveyHome, protectionJob } from '../sim/operations';
import { escapeHtml } from './format';
import { managementOf } from './overview';

/**
 * Reef health as the player can know it (M287, `docs/CONSERVATION_PROGRESS.md` section 1): each island's live coral as
 * it was measured, NOAA's surveys and the conservancy's own, never the simulation's true cover; what the measurements
 * show (fallen, risen, no clear change) and what they cannot yet (the years since the last one); how sure each is, in
 * plain words.
 */

/** One measurement of an island's live coral, all its reef to 30 m: the depths' covers weighed by their floor. */
export interface CoralPoint {
  day: number;
  year: number;
  /** Percent of the reef's floor, and its standard error, percentage points. */
  value: number;
  se: number;
  by: 'noaa' | 'conservancy';
}

/**
 * An island's live coral over its depths, each depth's cover weighed by its floor (km²) and its error combined as
 * independent strata's (as `reefWideOf` weighs the sanctuary); none where a depth was not counted, a part of the reef
 * not standing for the whole.
 */
function islandWide(reef: Reef, island: ReefIsland, cover: readonly (number | null | undefined)[], se: readonly (number | null | undefined)[]): { value: number; se: number } | null {
  const floor = stratumFloorKm2(reef);
  const i = REEF_ISLANDS.indexOf(island);
  let area = 0;
  let sum = 0;
  let variance = 0;
  for (let b = 0; b < DEPTH_BINS.length; b++) {
    const v = cover[b];
    if (v === null || v === undefined) return null;
    const a = floor[stratumIndex(i, b)] as number;
    area += a;
    sum += a * v;
    variance += (a * (se[b] ?? 0)) ** 2;
  }
  return area > 0 ? { value: sum / area, se: Math.sqrt(variance) / area } : null;
}

/** An island's live coral as measured, oldest first: NOAA's surveys (2015 to 2024, `MAUI_NUI_REEF`) and every count of the conservancy's own coral surveys it keeps. */
export function coralHistory(world: World, island: ReefIsland, noaaOnly = false): CoralPoint[] {
  const s = world.sanctuaries[0];
  const reef = s ? reefOf(s) : null;
  if (!reef) return [];
  const i = REEF_ISLANDS.indexOf(island);
  const out: CoralPoint[] = [];
  for (const sv of reef.surveys) {
    const strata = DEPTH_BINS.map((_, b) => sv.strata[stratumIndex(i, b)]);
    const w = islandWide(reef, island, strata.map((x) => x?.coverPercent), strata.map((x) => x?.sePercent));
    if (w) out.push({ day: sv.medianEpochDay, year: sv.year, ...w, by: 'noaa' });
  }
  for (const c of noaaOnly ? [] : world.operations.areaCounts as readonly AreaCount[]) {
    if (c.island !== island || c.kind !== 'cover' || c.by !== 'conservancy') continue;
    const w = islandWide(reef, island, c.value, c.se);
    if (w) out.push({ day: c.day, year: civilFromEpochDay(c.day).year, ...w, by: 'conservancy' });
  }
  return out.sort((a, b) => a.day - b.day);
}

/** What the measurements show of an island's coral: its last, how it stands against the earlier ones, and how long ago it was. */
export interface CoralVerdict {
  /** Fallen past twice the error since an earlier, higher measurement; risen past it since a lower one; no clear change; or no measurement at all. */
  trend: 'down' | 'up' | 'steady' | 'unknown';
  last: CoralPoint | null;
  /** The earlier measurement the trend is told against: the highest before the last for a fall, the lowest for a rise, the first for no change. */
  against: CoralPoint | null;
  /** Days since the last measurement. */
  daysSince: number;
}

/**
 * Whether two measurements differ past twice their combined standard error (about 95% sure it is not the counts'
 * chance alone: the same test the island card's and the month's report use, `changeWords`).
 */
const apart = (a: CoralPoint, b: CoralPoint): boolean => Math.abs(a.value - b.value) > 2 * Math.hypot(a.se, b.se);

/** With `noaaOnly`, what NOAA's record alone shows, before any count of the conservancy's. */
export function coralVerdict(world: World, island: ReefIsland, noaaOnly = false, asOf = epochDayOf(world)): CoralVerdict {
  // As the record stood on a day (a report's last), the measurements since left out.
  const points = coralHistory(world, island, noaaOnly).filter((p) => p.day <= asOf);
  const today = asOf;
  const last = points.at(-1) ?? null;
  if (!last) return { trend: 'unknown', last: null, against: null, daysSince: 0 };
  const daysSince = today - last.day;
  const before = points.slice(0, -1);
  // A fall told against the highest measured before (the reef's loss since its best measured state), a rise against
  // the lowest; a fall first where both hold (it is the reef's loss the player most needs to see).
  const high = before.reduce<CoralPoint | null>((m, p) => (!m || p.value > m.value ? p : m), null);
  const low = before.reduce<CoralPoint | null>((m, p) => (!m || p.value < m.value ? p : m), null);
  if (high && high.value > last.value && apart(high, last)) return { trend: 'down', last, against: high, daysSince };
  if (low && low.value < last.value && apart(low, last)) return { trend: 'up', last, against: low, daysSince };
  return { trend: before.length > 0 ? 'steady' : 'unknown', last, against: before[0] ?? null, daysSince };
}

/**
 * How sure a measurement is, in a word a player reads (JUDGED, a game's bands on the error as a share of the value: a
 * fifth or under close, two fifths or under rough, past that very rough, the 2024 survey's few sites on Maui's shallows
 * 12.9 ± on 24.0 among them).
 */
export function sureness(p: CoralPoint): 'close' | 'rough' | 'very rough' {
  const r = p.value > 0 ? p.se / p.value : Infinity;
  return r <= 0.2 ? 'close' : r <= 0.4 ? 'rough' : 'very rough';
}

/** The verdict in a few words: "Down from 23.5% in 2015", "Up from ...", "No clear change since 2016", "Not measured yet". */
export function verdictWords(v: CoralVerdict): string {
  if (!v.last) return 'Not measured yet';
  if (v.trend === 'down' && v.against) return `Down from ${v.against.value.toFixed(1)}% in ${v.against.year}`;
  if (v.trend === 'up' && v.against) return `Up from ${v.against.value.toFixed(1)}% in ${v.against.year}`;
  if (v.trend === 'steady' && v.against) return `No clear change since ${v.against.year}`;
  return `Measured once, in ${v.last.year}`;
}

/**
 * The newest measurement against the one just before it, where the newest is the conservancy's own (M289, the playtest's
 * first point: "Lead with the comparison to the previous survey, whether uncertainty allows a conclusion, and what that
 * means for my next decision. Keep historical decline underneath"): down or up past twice their combined error, else no
 * change the counts can tell. As the record stood on a day (`asOf`). None before the conservancy has counted the island.
 */
export interface LatestChange {
  last: CoralPoint;
  before: CoralPoint;
  trend: 'down' | 'up' | 'steady';
}
export function latestChange(world: World, island: ReefIsland, asOf = epochDayOf(world)): LatestChange | null {
  const points = coralHistory(world, island).filter((p) => p.day <= asOf);
  const last = points.at(-1);
  const before = points.at(-2);
  if (!last || !before || last.by !== 'conservancy') return null;
  return { last, before, trend: !apart(before, last) ? 'steady' : last.value < before.value ? 'down' : 'up' };
}

/** The newest survey against the one before, in a few words: "Down from 18.7% in 2024", "No clear change from 18.7% in 2024". */
export function latestWords(c: LatestChange): string {
  const was = `${c.before.value.toFixed(1)}% in ${c.before.year}`;
  return c.trend === 'down' ? `Down from ${was}` : c.trend === 'up' ? `Up from ${was}` : `No clear change from ${was}`;
}

/**
 * What a survey's answer means for the next decision (M289): a change within the counts' error tells nothing yet, so
 * the next count is the one to plan and the lever stays the conservancy's own; a fall past it asks for what the
 * conservancy can act on, the fishing on the grazing fish (the heat it cannot); a rise past it asks to hold course and
 * count again to confirm it. The coral answers over years (M47), so no single count is told as an effect of the work.
 */
export function nextStepWords(c: LatestChange): string {
  if (c.trend === 'steady') return `${Math.abs(c.last.value - c.before.value).toFixed(1)} points ${c.last.value < c.before.value ? 'lower' : 'higher'}, within the counts' error: no change can be told yet. Count again in a year; meanwhile protecting the fish that graze the reef is the lever you hold.`;
  if (c.trend === 'down') return 'Still falling, past the counts\' error. Act where you can: the fishing on the fish that graze it, which keep the algae off the coral (a protected area or patrols); the heat you cannot.';
  return 'Rising, past the counts\' error. Keep what you are doing, and count again in a year to confirm it: one rise can be a good year.';
}

/**
 * A survey's answer in plain words (M305, the field loop's F4, the third playtest's: "Survey complete: no clear change in
 * coral. The reef remains degraded. Your protection project is underway; recovery will take longer."): the verdict, the
 * reef's state on its record, and what the conservancy's own work there means for it, with no figure and no error (those
 * are the research details, `nextStepWords` among them). "No clear change" is never "steady": the counts cannot tell a
 * change, which is not the same as none.
 */
export type Protecting = 'inForce' | 'proposed' | 'patrolled' | 'none' | null;
export function plainFinding(c: LatestChange, record: CoralVerdict, name: string, protecting: Protecting): { headline: string; lines: string[] } {
  // "Still" only where the record fell before it (its review: a first fall told as going on).
  // Within the counts' error, which way the count moved and that it is too little to call (M320, the casual year's H2:
  // "no clear change" read as nothing happening, Maui 19% to 22% and Lānaʻi 13% to 9% alike); never "steady" (M305).
  const within = c.trend !== 'steady' || !c.last || !c.before ? `no clear change in ${name}'s coral` : c.last.value > c.before.value ? `${name}'s coral counted a little higher than last time, too little to call a rise yet`
    : c.last.value < c.before.value ? `${name}'s coral counted a little lower than last time, too little to call a fall yet` : `${name}'s coral counted the same as last time`;
  const headline = c.trend === 'down' ? `Survey complete: ${name}'s coral ${record.trend === 'down' ? 'is still falling' : 'has fallen'}.` : c.trend === 'up' ? `Survey complete: ${name}'s coral has risen.` : `Survey complete: ${within}.`;
  // Against the record's best, as `coralVerdict` reads a fall (its review: "below what NOAA first counted" where it was above it).
  const state = record.trend === 'down' && c.trend !== 'up' ? 'The reef is still degraded: its coral is below its best on the record.' : '';
  // The work told only as it stands now, on a page of now (`null`: a page of the past, its review: today's protection had
  // been told against a count taken before it).
  const work = protecting === null ? ''
    : protecting === 'inForce' ? 'Your protected area is now in force; the coral recovers over years, not in one.'
      : protecting === 'proposed' ? 'Your protected area is now in consultation; recovery comes years after it is in force.'
        : protecting === 'patrolled' ? 'Your patrol is on the water now; the coral recovers over years, not in one.'
          : c.trend === 'down' ? 'Act where you can: protect the fish that graze it, which keep the algae off the coral.'
            : c.trend === 'up' ? 'Keep on, and count again in a year to confirm it: one rise can be a good year.'
              // Told as what it decides (M314, the fourth playtest's: "'no clear change' and 'count again in a year'...
              // emotionally weak"): the baseline protection starts from.
              // Its review: no count is told as the work's effect (`nextStepWords`), the coral answering over years.
              : 'Your protection starts from here: the fish that graze the reef are the lever you hold, and the coral answers over years.';
  return { headline, lines: [state, work].filter(Boolean) };
}

/**
 * Whether the last measurement is recent enough to stand for the reef now (JUDGED, a game's: within a year, the span
 * the goals hold a count known for, `goals.ts`'s `KNOWN_DAYS`; the coral moves little in a year without heat, M47).
 */
export const RECENT_DAYS = 365;
const recent = (v: CoralVerdict): boolean => v.last !== null && v.daysSince < RECENT_DAYS;

/** Since the last measurement, plainly: recovery or further loss not known without a new survey (the brief's 1.9: no new measurement is never stability). */
export function sinceWords(v: CoralVerdict): string {
  if (!v.last) return 'No survey has counted it';
  return recent(v) ? `Measured ${dateLabel(v.last.day)}` : `Since ${v.last.year}: unknown, a new survey is needed`;
}

/**
 * A small chart of an island's coral as measured (the brief's 1.3): each measurement a dot on its date with its error
 * as a bar, NOAA's hollow and the conservancy's filled, joined by a line; from the last to today a dashed line ending
 * in a question mark, as nothing is known of those years. Inline SVG, sized to its box, colors from the page's.
 */
export function coralChartSvg(points: readonly CoralPoint[], today: number, label: string): string {
  if (points.length === 0) return '';
  const w = 300;
  const h = 120;
  const pad = { l: 30, r: 14, t: 10, b: 20 };
  const x0 = Math.min((points[0] as CoralPoint).day, today) - 120;
  const x1 = today + 60;
  const top = Math.max(10, Math.ceil(Math.max(...points.map((p) => p.value + p.se)) / 10) * 10);
  const x = (d: number): number => pad.l + ((d - x0) / (x1 - x0)) * (w - pad.l - pad.r);
  const y = (v: number): number => pad.t + (1 - Math.max(0, v) / top) * (h - pad.t - pad.b);
  const f = (n: number): string => n.toFixed(1);
  const grid = [0, top / 2, top].map((v) => `<line x1="${pad.l}" x2="${w - pad.r}" y1="${f(y(v))}" y2="${f(y(v))}" class="grid"/><text x="${pad.l - 4}" y="${f(y(v) + 3)}" class="axis" text-anchor="end">${v}%</text>`).join('');
  // A label on the first year shown, each fifth year, and today's, none within two years of another (review: 2025 and 2026 overlapped).
  const first = civilFromEpochDay(x0 + 120).year;
  const now = civilFromEpochDay(today).year;
  const years = [first, ...Array.from({ length: now - first }, (_, k) => first + k + 1).filter((yr) => yr % 5 === 0 && yr - first >= 2 && now - yr >= 2), now].filter((yr, k, all) => all.indexOf(yr) === k);
  const ticks = years.map((yr, k) => `<text x="${f(Math.min(x(epochDayFromCivil(yr, 7, 1)), w - 2))}" y="${h - 5}" class="axis" text-anchor="${k === years.length - 1 ? 'end' : 'middle'}">${yr}</text>`).join('');
  const line = points.map((p, k) => `${k === 0 ? 'M' : 'L'}${f(x(p.day))} ${f(y(p.value))}`).join(' ');
  const dots = points.map((p) => `<line x1="${f(x(p.day))}" x2="${f(x(p.day))}" y1="${f(y(p.value - p.se))}" y2="${f(y(p.value + p.se))}" class="err"/><circle cx="${f(x(p.day))}" cy="${f(y(p.value))}" r="3.5" class="${p.by === 'noaa' ? 'noaa' : 'ours'}"><title>${p.year}: ${p.value.toFixed(1)}% ± ${p.se.toFixed(1)}, ${p.by === 'noaa' ? 'NOAA\'s divers' : 'your survey'}</title></circle>`).join('');
  const last = points.at(-1) as CoralPoint;
  // Past the year a count stands for today (`RECENT_DAYS`, the tiles' own; review 33: 180 days, from nothing, had the
  // chart's "?" beside a tile still reading the count as today's).
  const gap = today - last.day >= RECENT_DAYS ? `<path d="M${f(x(last.day))} ${f(y(last.value))} L${f(x(today))} ${f(y(last.value))}" class="unknown"/><text x="${f(x(today))}" y="${f(y(last.value) - 6)}" class="q" text-anchor="middle">?</text>` : '';
  return `<svg class="coral-chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label}">${grid}${ticks}<path d="${line}" class="trend"/>${gap}${dots}</svg>`;
}

/** An island's tile on Manage's home: its last measured coral, the verdict and the years since; a press opens its island. */
function tileHtml(world: World, island: ReefIsland): string {
  const v = coralVerdict(world, island);
  // Led by the conservancy's newest survey against the one before where there is one (M289), the record's verdict under it.
  const c = latestChange(world, island);
  const trend = c ? c.trend : v.trend;
  const arrow = { down: '▼', up: '▲', steady: '▬', unknown: '?' }[trend];
  // Its date where measured within the year, else in three words that it is not known since (M296, the roadmap's R6:
  // "Since 2024: unknown, a new survey is needed" four times over; its review: none at all read an old count as today's);
  // the record's verdict only where it tells otherwise than the latest count's.
  const when = !v.last ? sinceWords(v) : recent(v) ? sinceWords(v) : `Since ${v.last.year}: unknown`;
  const since = v.last && c && c.trend !== v.trend ? `${when}; over the record, ${verdictWords(v).toLowerCase()}` : when;
  return `<button class="health-tile trend-${trend}" data-act="area" data-id="${island}">
    <span class="tile-name">${escapeHtml(ISLAND_NAMES[island])}</span>
    <strong class="tile-value">${v.last ? `${v.last.value.toFixed(0)}%` : '—'}</strong>
    <span class="tile-trend"><span aria-hidden="true">${arrow}</span> ${escapeHtml(c ? latestWords(c) : verdictWords(v))}</span>
    ${since ? `<span class="tile-since">${escapeHtml(since)}</span>` : ''}</button>`;
}

/** The sanctuary in a sentence: how many islands' coral the record shows fallen, risen or unchanged, and how long since anyone measured. */
export function sanctuaryHeadline(world: World): string {
  const vs = REEF_ISLANDS.map((island) => coralVerdict(world, island));
  const down = vs.filter((v) => v.trend === 'down').length;
  const up = vs.filter((v) => v.trend === 'up').length;
  const parts = [down > 0 ? `coral has fallen on ${down} of ${REEF_ISLANDS.length} islands` : '', up > 0 ? `risen on ${up}` : ''].filter(Boolean);
  const lead = parts.length > 0 ? `${parts.join(', ')} from the highest measured` : 'no clear change in the coral on the record';
  // The islands not measured within the year (`RECENT_DAYS`), and the oldest of their last measurements (the brief's 1.9).
  const stale = vs.filter((v) => v.last && !recent(v));
  const oldest = Math.min(...stale.map((v) => (v.last as CoralPoint).year));
  const last = stale.length === 0 ? '' : stale.length === REEF_ISLANDS.length ? `; nothing is known of it since ${oldest}${stale.some((v) => (v.last as CoralPoint).year !== oldest) ? ' or later' : ''}` : `; ${stale.length} ${stale.length === 1 ? 'island' : 'islands'} not measured since ${oldest}${stale.some((v) => (v.last as CoralPoint).year !== oldest) ? ' or later' : ''}`;
  return `${lead[0]!.toUpperCase()}${lead.slice(1)}${last}.`;
}

/** The threats on an island now, each in a few words, the worst first: its grazing fish's fishing as its rules and patrols leave it, the heat, its bleaching as assessed. */
export function threatsOn(world: World, island: ReefIsland): { tone: 'bad' | 'warn' | 'good'; text: string }[] {
  const out: { tone: 'bad' | 'warn' | 'good'; text: string }[] = [];
  const s = world.sanctuaries[0];
  const m = managementOf(world);
  const x = m?.islands.find((y) => y.island === island);
  if (x) {
    const level = x.patrol.level;
    const taken = Math.round(100 * (x.patrol.takenAnyway[level] ?? 0));
    if (x.herbivoresProtected) out.push({ tone: 'good', text: `Grazing fish protected; ${taken}% of the forbidden catch still taken` });
    else if (level > 0) out.push({ tone: 'warn', text: `Grazing fish fished; patrolled, ${taken}% of the catch the rules forbid still taken` });
    else out.push({ tone: 'bad', text: 'Grazing fish fished under Hawaiʻi\'s general rules' });
  }
  const heat = s ? heatStressToday(s, epochDayOf(world)) : null;
  if (heat && heat.level !== 'No stress') out.push({ tone: heat.dhw >= HEAT_ALERT_DHW ? 'bad' : 'warn', text: `Heat stress: ${heat.level.toLowerCase()} (NOAA Coral Reef Watch's measure)` });
  // An assessment within the year alone (review 33: one of a heat long past had stood as the island's worst threat for good).
  const bleached = (world.operations.areaCounts as readonly AreaCount[]).filter((c) => c.island === island && c.kind === 'bleached' && c.day > epochDayOf(world) - RECENT_DAYS).at(-1);
  if (bleached) {
    const known = bleached.value.filter((v): v is number => v !== null);
    if (known.length > 0) out.push({ tone: 'bad', text: `Up to ${Math.round(Math.max(...known))}% of its coral bleached when assessed, ${dateLabel(bleached.day)}` });
  }
  return out;
}

/** An island's bottom as NOAA's divers found it, 2015 to 2024 (`REEF_BOTTOM`), its depths weighed by their floor: turf and macroalgae, percent of the bottom. */
function algaeOn(island: ReefIsland, reef: Reef): { turf: number; macroalgae: number } {
  const floor = stratumFloorKm2(reef);
  const i = REEF_ISLANDS.indexOf(island);
  let area = 0;
  let turf = 0;
  let macro = 0;
  DEPTH_BINS.forEach((_, b) => {
    const k = stratumIndex(i, b);
    const a = floor[k] as number;
    const x = REEF_BOTTOM[k];
    if (!x) return;
    area += a;
    turf += a * x.turfPercent;
    macro += a * x.macroalgaePercent;
  });
  return area > 0 ? { turf: turf / area, macroalgae: macro / area } : { turf: 0, macroalgae: 0 };
}

/** One island's panel on the reef health page: the figure, the verdict, the chart, how sure, the grazing fish, the algae and the threats. */
export function islandHealthHtml(world: World, island: ReefIsland): string {
  const s = world.sanctuaries[0];
  const reef = s ? reefOf(s) : null;
  if (!reef) return '';
  const points = coralHistory(world, island);
  const v = coralVerdict(world, island);
  const c = latestChange(world, island);
  const herb = knownAt(world, island, 'herbivores', 0);
  const algae = algaeOn(island, reef);
  const threats = threatsOn(world, island);
  return `<section class="island-health trend-${v.trend}">
    <h4>${escapeHtml(ISLAND_NAMES[island])}</h4>
    <div class="health-figures">
      <div><span class="label">Live coral</span><strong>${v.last ? `${v.last.value.toFixed(1)}%` : '—'}</strong><span class="row-note">${v.last ? `of the reef's floor, ${escapeHtml(v.last.by === 'noaa' ? 'NOAA\'s divers' : 'your survey')}, ${v.last.year}; a ${sureness(v.last)} measure (± ${v.last.se.toFixed(1)})` : 'never measured'}</span></div>
      ${c ? `<div class="trend-${c.trend}"><span class="label">Since the last count</span><strong class="verdict">${escapeHtml(latestWords(c))}</strong><span class="row-note">${escapeHtml(sinceWords(v))}</span></div>`
        : `<div><span class="label">What it shows</span><strong class="verdict">${escapeHtml(verdictWords(v))}</strong><span class="row-note">${escapeHtml(sinceWords(v))}</span></div>`}
    </div>
    ${c ? `<p class="next-step">${escapeHtml(nextStepWords(c))}</p><p class="row-note">Over the whole record: ${escapeHtml(verdictWords(v).toLowerCase())}.</p>` : ''}
    ${coralChartSvg(points, epochDayOf(world), `${ISLAND_NAMES[island]}'s live coral as measured`)}
    <ul class="health-facts">
      <li><span class="label">Grazing fish</span> ${herb ? `${Math.round(herb.value)} kg a hectare ${herb.se !== null ? `± ${Math.round(herb.se)}` : ''}, ${escapeHtml(herb.by === 'community' ? 'your volunteers' : 'your survey')}, ${escapeHtml(dateLabel(herb.day))}` : 'not counted by island yet: a grazing fish count or volunteers would tell'}</li>
      <li><span class="label">Algae</span> turf ${algae.turf.toFixed(0)}%, seaweed ${algae.macroalgae.toFixed(0)}% of the bottom <span class="row-note">NOAA's surveys, 2015 to 2024 on average</span></li>
      ${threats.map((t) => `<li class="tone-${t.tone}"><span class="label">Threat</span> ${escapeHtml(t.text)}</li>`).join('')}
    </ul>
  </section>`;
}

/**
 * The reef health page (the brief's section 1): every island's measured coral over time, what it shows and how sure,
 * its grazing fish, algae and threats; how the figures are made behind a fold.
 */
export function reefHealthHtml(world: World): string {
  return `<h3>Reef health</h3>
    <p class="why">${escapeHtml(sanctuaryHeadline(world))}</p>
    <p class="more">The whole reef underwater: <button class="link" data-act="reef-then" data-on="0">today</button> <button class="link" data-act="reef-then" data-on="1">on your first day</button></p>
    ${REEF_ISLANDS.map((island) => islandHealthHtml(world, island)).join('')}
    <details class="advanced"><summary>How these figures are made</summary>
      <p class="note">Live coral is the share of the reef's floor, to 30 m, covered by living coral, as divers counted it at their sites: each depth's count weighed by how much reef lies at that depth. NOAA's surveys (hollow dots) are the record before you; your own surveys (filled) add to it. The bars are each count's standard error. A change is told only where two counts differ by more than twice their combined error; a smaller difference may be the sites' chance. Between counts nothing is known: a reef not measured since 2024 may have recovered or declined since.</p>
    </details>`;
}

/**
 * The threats on the reef in a line (the brief's 10.1, "what is threatening it?"): how many islands' grazing fish are
 * fished with no protection or patrol, and the heat as NOAA Coral Reef Watch measures it today.
 */
export function sanctuaryThreats(world: World): string {
  const p = pressureEased(world);
  const open = p.of - p.eased;
  const s = world.sanctuaries[0];
  const heat = s ? heatStressToday(s, epochDayOf(world)) : null;
  const fishing = open > 0 ? `grazing fish fished unprotected on ${open} of ${p.of} islands` : 'the grazing fish protected or patrolled on every island';
  return `${fishing[0]!.toUpperCase()}${fishing.slice(1)}; ${heat && heat.level !== 'No stress' ? `heat stress: ${heat.level.toLowerCase()}` : 'no heat stress today'}.`;
}

/** Manage's home's first section: the headline, the four islands as tiles, the way to the full page. */
export function healthSectionHtml(world: World): string {
  return `<section class="home-section"><h3>Reef health</h3>
    <p class="why">${escapeHtml(sanctuaryHeadline(world))}</p>
    <div class="health-tiles">${REEF_ISLANDS.map((island) => tileHtml(world, island)).join('')}</div>
    <p class="threats-now"><span class="label">Threats now</span> ${escapeHtml(sanctuaryThreats(world))}</p>
    <p class="more"><button class="link" data-act="open" data-kind="health">Reef health and history</button></p></section>`;
}

/** One line of the conservancy's work: what it is, where it stands, and what it changed now (never the reef's answer, which only a later measurement shows). */
export interface ImpactLine {
  what: string;
  status: string;
  tone: 'good' | 'warn' | 'info';
  effect: string;
  /** A protected area's progress in stages (M289). */
  stages?: Stage[];
}

/**
 * The conservancy's work and its operational effect (the brief's 2.1 and 2.3): the protected areas in force and in
 * process, the patrols and the catch they cut, the surveys home and the islands they measured, the volunteers, the
 * boats. Each effect is what the action did at once, from the rules the model runs (`management.ts`); what it may do
 * to the reef is left to the measurements.
 */
export function impactOf(world: World): ImpactLine[] {
  const out: ImpactLine[] = [];
  const m = managementOf(world);
  const ops = world.operations;
  const today = epochDayOf(world);
  for (const x of m?.islands ?? []) {
    const open = Math.round(100 * (x.patrol.takenAnyway[0] ?? 0));
    const stages = protectionStages(world, x.island) ?? undefined;
    if (x.herbivoresProtected) out.push({ what: `${x.name}: Herbivore Protection Area`, status: 'In force', tone: 'good', stages, effect: `No parrotfish, kala, nenue or manini may be taken on ${x.name}'s reef; ${Math.round(100 * (x.patrol.takenAnyway[x.patrol.level] ?? 0))}% of that catch still taken against the law` });
    const job = ruleJob(world, x.island);
    if (job) out.push({ what: `${x.name}: Herbivore Protection Area`, status: today < (job.phaseEnds[0] as number) ? 'Consulting' : 'With the State', tone: 'warn', stages, effect: `In force about ${dateLabel(job.phaseEnds[1] as number)}; nothing changes on the water until then` });
    if (x.patrol.level > 0) out.push({ what: `${x.name}: patrols`, status: 'Active', tone: 'good', effect: `${Math.round(100 * (x.patrol.takenAnyway[x.patrol.level] ?? 0))}% of the catch the rules forbid still taken, against ${open}% unpatrolled${x.patrol.until !== null ? `; to ${dateLabel(x.patrol.until)}` : ''}` });
  }
  const year = civilFromEpochDay(today).year;
  // The surveys alone: a shore cleanup is a mission, no monitoring (M307).
  const surveys = ops.finished.filter((f) => isSurveyHome(f) && civilFromEpochDay(f.endDay).year === year);
  const atSeaNow = ops.jobs.filter((j) => j.kind === 'survey' && j.survey !== 'shoreCleanup');
  if (surveys.length > 0 || atSeaNow.length > 0) {
    const counted = [...new Set(surveys.map((f) => (f.island ? ISLAND_NAMES[f.island] : 'the whole sanctuary')))];
    out.push({ what: 'Monitoring', status: atSeaNow.length > 0 ? 'Survey at sea' : 'Done', tone: atSeaNow.length > 0 ? 'warn' : 'good', effect: surveys.length > 0 ? `${surveys.length} ${surveys.length === 1 ? 'survey' : 'surveys'} home this year: ${counted.join(', ')}` : 'The first of the year at sea' });
  }
  REEF_ISLANDS.forEach((island, i) => {
    const since = ops.community[i];
    if (since !== null && since !== undefined) out.push({ what: `${ISLAND_NAMES[island]}: volunteers`, status: 'Counting', tone: 'good', effect: `Its grazing fish counted every month since ${dateLabel(since)}, no boat needed` });
    else if (ops.jobs.some((j) => j.kind === 'community' && j.island === island)) out.push({ what: `${ISLAND_NAMES[island]}: volunteers`, status: 'Training', tone: 'warn', effect: 'Your liaison trains them with the community; counting begins after' });
  });
  if (ops.boats > 1) out.push({ what: 'A second boat', status: 'In the water', tone: 'good', effect: 'Two surveys can be at sea at once' });
  else if (ops.projects.some((p) => p.kind === 'boat')) out.push({ what: 'A second boat', status: 'Ordered', tone: 'warn', effect: 'Two surveys at sea at once once it comes' });
  return out;
}

/**
 * The share of the grazing fish's fishing eased: islands with a protected area in force or a patrol on the water, of the
 * four; and those whose protected area is proposed and on its way, not yet in force (M289, the playtest's fourth point:
 * a proposal counted as under way, not as nothing, the months before it acts).
 */
export function pressureEased(world: World): { eased: number; underWay: number; of: number } {
  const m = managementOf(world);
  const islands = m?.islands ?? [];
  const eased = islands.filter((x) => x.herbivoresProtected || x.patrol.level > 0);
  const underWay = islands.filter((x) => !eased.includes(x) && ruleJob(world, x.island)).length;
  return { eased: eased.length, underWay, of: REEF_ISLANDS.length };
}

/** The pressure meter: a segment lit for each island eased, half lit for each under way, its words beside it. */
export function pressureMeterHtml(p: { eased: number; underWay: number; of: number }, label = 'Fishing on the grazing fish eased'): string {
  const words = `${p.eased} of ${p.of} islands${p.underWay > 0 ? `, ${p.underWay} under way` : ''}`;
  return `<div class="meter" role="img" aria-label="${p.eased} of ${p.of} islands' grazing fish protected or patrolled${p.underWay > 0 ? `, ${p.underWay} more under way` : ''}"><span class="meter-label">${escapeHtml(label)}</span><span class="meter-bar">${Array.from({ length: p.of }, (_, k) => `<i class="${k < p.eased ? 'on' : k < p.eased + p.underWay ? 'part' : ''}"></i>`).join('')}</span><span class="meter-value">${words}</span></div>`;
}

/** An island's Herbivore Protection Area on its way, a rule's job in consultation or with the State. */
const ruleJob = (world: World, island: ReefIsland) => protectionJob(world.operations, island);

/** One stage of a protection's progress: done, under way now, or still ahead, with its date or what it waits on. */
export interface Stage { name: string; state: 'done' | 'now' | 'next'; when: string }

/**
 * A Herbivore Protection Area's progress in stages (M289, the playtest's fourth point: "consultation, rule-making,
 * enforcement, and ecological response"), each counted as it is reached: proposed, the communities consulted (the
 * liaison's months), the rule made by the State, enforced (patrols on the water keep it: unpatrolled, most of the
 * forbidden catch is still taken, `management.ts`), and the reef's answer, which only later counts show (fish over a
 * few years, coral over many, M287). None where no area is proposed or in force.
 */
export function protectionStages(world: World, island: ReefIsland): Stage[] | null {
  const x = managementOf(world)?.islands.find((y) => y.island === island);
  const job = ruleJob(world, island);
  if (!x || (!job && !x.herbivoresProtected)) return null;
  const today = epochDayOf(world);
  const inForce = x.herbivoresProtected;
  const consulted = inForce || (job ? today >= (job.phaseEnds[0] as number) : false);
  return [
    { name: 'Proposed', state: 'done', when: job ? dateLabel(job.startDay) : '' },
    { name: 'Consultation', state: consulted ? 'done' : 'now', when: job ? `${consulted ? 'ended' : 'to'} ${dateLabel(job.phaseEnds[0] as number)}` : '' },
    { name: 'Rule made', state: inForce ? 'done' : consulted ? 'now' : 'next', when: inForce ? 'in force' : job ? `about ${dateLabel(job.phaseEnds[1] as number)}` : '' },
    { name: 'Enforced', state: x.patrol.level > 0 ? 'now' : 'next', when: x.patrol.level > 0 ? `patrols${x.patrol.until !== null ? ` to ${dateLabel(x.patrol.until)}` : ''}` : 'patrols keep it' },
    { name: 'Reef\'s answer', state: inForce ? 'now' : 'next', when: 'fish over a few years, coral over many' },
  ];
}

/** The stages as a strip of chips, each its name and date. */
function stagesHtml(stages: readonly Stage[]): string {
  return `<ol class="stages">${stages.map((g) => `<li class="stage stage-${g.state}"><span class="stage-name">${escapeHtml(g.name)}</span>${g.when ? `<span class="stage-when">${escapeHtml(g.when)}</span>` : ''}</li>`).join('')}</ol>`;
}

/** Manage's home's second section: the work as a list of short rows, each its status and what it changed; the line that keeps it apart from the reef's recovery. */
export function impactSectionHtml(world: World): string {
  const lines = impactOf(world);
  const p = pressureEased(world);
  const rows = lines.map((l) => `<li><span class="impact-what">${escapeHtml(l.what)}</span><span class="chip tone-${l.tone}">${escapeHtml(l.status)}</span><span class="impact-effect">${escapeHtml(l.effect)}</span>${l.stages ? stagesHtml(l.stages) : ''}</li>`).join('');
  return `<section class="home-section"><h3>Your impact</h3>
    ${pressureMeterHtml(p)}
    ${rows ? `<ul class="impact">${rows}</ul><p class="note">Your work is under way. Whether the reef recovers shows only in later measurements: fish over a few years, coral over many.</p>` : '<p class="note">Nothing under way yet. Choose a priority below.</p>'}
  </section>`;
}
